/**
 * The staff-side chrome. Structurally a sibling of `AppShell`, deliberately not
 * a variant of it.
 *
 * WHY A SEPARATE SHELL: AppShell's right rail is a bet slip and its topbar is a
 * Deposit button and a playable balance. None of that belongs on a screen where
 * the operator is looking at other people's money. Sharing one shell behind a
 * flag would mean every future customer-facing addition needs an "unless
 * admin" branch, and the first one that gets forgotten shows a customer widget
 * on a compliance screen.
 *
 * THE BANNER IS NOT DECORATION. The single visual difference an operator must
 * never miss is which side of the product they are on — the two look alike
 * enough that a tab left open is genuinely ambiguous. So the staff header
 * carries a persistent marker and the operator's own role.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMe } from '@/hooks/useMe';

const Icon = {
  alerts: <><path d="M12 3 2.5 20h19L12 3Z" /><path d="M12 10v4M12 17.2v.1" /></>,
  params: <><circle cx="12" cy="12" r="3.2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></>,
  users: <><circle cx="9" cy="8.5" r="3.4" /><path d="M2.5 20c0-3.4 2.9-5.2 6.5-5.2s6.5 1.8 6.5 5.2" /><path d="M16.5 5.6a3.4 3.4 0 0 1 0 6.3M18 14.4c2.2.6 3.5 2.1 3.5 4.3" /></>,
  reports: <><rect x="3" y="3" width="18" height="18" rx="2.5" /><path d="M8 16v-4M12 16V8M16 16v-6" /></>,
  markets: <><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M3 9h18M9 9v11M14.5 13l1.6 1.6 2.9-2.9" /></>,
  audit: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 12h6M9 16h4" /></>,
  engine: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 1.9" /></>,
  quarantine: <><path d="M12 2.8 3.5 6.2v5.4c0 4.7 3.5 8.2 8.5 9.6 5-1.4 8.5-4.9 8.5-9.6V6.2L12 2.8Z" /><path d="M9 12h6" /></>,
  corrections: <><path d="M3.5 12a8.5 8.5 0 0 1 14.6-5.9L21 9" /><path d="M21 4.5V9h-4.5" /><path d="M20.5 12a8.5 8.5 0 0 1-14.6 5.9L3 15" /><path d="M3 19.5V15h4.5" /></>,
  back: <><path d="M15 6l-6 6 6 6" /></>,
} satisfies Record<string, ReactNode>;

function Glyph({ icon, size = 18 }: { icon: ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {icon}
    </svg>
  );
}

const NAV = [
  { to: '/admin/alerts', label: 'Ops alerts', icon: Icon.alerts },
  { to: '/admin/params', label: 'Risk limits', icon: Icon.params },
  { to: '/admin/markets', label: 'Market visibility', icon: Icon.markets },
  { to: '/admin/quarantines', label: 'Fixture quarantines', icon: Icon.quarantine },
  { to: '/admin/users', label: 'Users', icon: Icon.users },
  { to: '/admin/resettlement', label: 'Corrections', icon: Icon.corrections },
  { to: '/admin/reports', label: 'Reports', icon: Icon.reports },
  { to: '/admin/engine', label: 'Odds engine', icon: Icon.engine },
  { to: '/admin/audit', label: 'Audit log', icon: Icon.audit },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
    isActive ? 'bg-brand-teal/10 text-brand-teal' : 'text-white/65 hover:bg-white/[0.04] hover:text-white'
  }`;

export function AdminShell() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const role = me?.user.role ?? null;

  return (
    <div className="h-screen flex flex-col">
      <header className="shrink-0 h-[60px] grid grid-cols-[auto_1fr_auto] lg:grid-cols-[240px_1fr_auto] items-center gap-3 border-b border-white/[0.07] bg-gradient-to-b from-brand-navy2 to-brand-navy px-4 lg:pl-4 lg:pr-6">
        <div className="flex items-center gap-2.5 pl-1">
          <span className="w-8 h-8 rounded-[9px] grid place-items-center bg-white/[0.07] border border-white/15 text-white font-display font-extrabold text-[17px] leading-none">P</span>
          <span className="font-display font-extrabold text-[15px] tracking-[0.06em] text-white">
            PUNTSAVE <span className="text-brand-gold">STAFF</span>
          </span>
        </div>

        <div className="hidden sm:flex">
          <span className="rounded-md border border-brand-gold/30 bg-brand-gold/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-brand-gold">
            Internal console · live production data
          </span>
        </div>

        <div className="flex items-center gap-2 justify-end">
          {role && (
            <span className="hidden sm:inline-flex rounded-md border border-white/15 bg-white/[0.06] px-2 py-1 text-[11px] font-medium text-white/70">
              {role}
            </span>
          )}
          <button
            onClick={() => navigate('/bets')}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.05] px-3 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
          >
            <Glyph icon={Icon.back} size={16} /> Exit
          </button>
        </div>
      </header>

      <div className="flex-1 grid overflow-hidden lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="hidden lg:flex flex-col gap-1 overflow-y-auto border-r border-white/[0.07] bg-brand-navy2/60 p-3">
          {NAV.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} className={linkClass}>
              <Glyph icon={icon} /> {label}
            </NavLink>
          ))}
          <p className="mt-auto px-3 pt-6 text-[11px] leading-relaxed text-white/25">
            Every action here is checked again by the API against your role, and
            config writes are recorded in the audit log against your account.
          </p>
        </aside>

        <main className="overflow-y-auto">
          {/* Mobile nav — the desktop sidebar is hidden below lg. */}
          <div className="lg:hidden flex gap-1.5 overflow-x-auto border-b border-white/[0.07] px-3 py-2">
            {NAV.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    isActive ? 'bg-brand-teal/10 text-brand-teal' : 'text-white/55 hover:bg-white/[0.04]'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>

          <div className="mx-auto w-full max-w-6xl px-4 py-6 lg:px-7">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
